/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import {
  filter,
  map,
  sortBy,
  isArray,
  isEmpty,
  toUpper,
  partition,
  groupBy
} from 'lodash';

import { UGMA_UTMA } from 'services/AccountTypes';
import {
  isActiveAdvisory,
  isBrokerage,
  isTracPlan,
  PRODUCTS
} from './Products';

export const BASE_TRANSACTION_TYPE = 1;
export const BLOCK_VIEW_ACCESS = 11;
export const BROKERAGE_ADVANTAGE_SERVICE_ID = 62;
export const VRU_STOP_ALL = 41;
export const OPTIONS_ENABLED = 36;
export const PURCHASE_TRANSACTION_TYPE = 130;
export const REDEEM_TRANSACTION_TYPE = 131;
export const EXCHANGE_TRANSACTION_TYPE = 132; // we also use this for roth conversion business events as they map to the same event type
export const WIRE_DELIVERY_TYPE = 5;
export const ELECTRONIC_DELIVERY_TYPE = 6;
export const ADDRESS_OF_RECORD_DELIVERY_TYPE = 7;
export const STOPPED_TRANSFER = 31;

export const STOP_PURCHASE = 30;
export const DISTRIBUTION_TO_SCHOOL_TYPE = 137;

export const arrayOfAccountDescriptors = (accounts, excludePurged = true) => {
  const filtered = excludePurged
    ? filter(accounts, (a) => {
        return !a.purgedAccount;
      })
    : accounts;

  return map(filtered, (a) => {
    return {
      accountId: a.accountId,
      accountNumber: a.accountNumber,
      accountChecksum: a.accountChecksum,
      productType: a.productType,
      accountTypeId: a.accountTypeId
    };
  }).sort((x, y) =>
    x.accountId < y.accountId ? -1 : x.accountId > y.accountId ? 1 : 0
  );
};

export const getAccountDescriptor = (account) => ({
  accountTypeId: account.accountTypeId,
  accountId: account.accountId,
  accountNumber: account.accountNumber,
  accountChecksum: account.accountChecksum,
  productType: account.productType,
  trpCode: account.trpCode,
  cusipId: account.cusipId,
  ticker: account.ticker,
  balanceValue: account.balanceValue
});

export const getAgreementKey = (account) => {
  return {
    productType: {
      enumerationID: account.productType,
      enumerationName: PRODUCTS[account.productType]
    },
    '@Type': 'AgreementKey',
    agreementID: account.accountId,
    agreementNumber: account.accountNumber,
    checkSum: account.accountChecksum,
    trpcode: account.trpCode
  };
};

export const isRegistrationPOA = (account, customerBusinessPartyId) => {
    return (
      account.agreementParties &&
      account.agreementParties.some((party) => {
        return (
          party &&
          party.customer &&
          party.customer.businessPartyId === customerBusinessPartyId &&
          party.role === 'AGNT'
        );
      })
    );
}

export const isRegistrationDeceased = (account, customerBusinessPartyId) => {

  return (
    account.agreementParties &&
    account.agreementParties.some((party) => {
      return (
        party &&
        party.customer &&
        party.customer.businessPartyId === customerBusinessPartyId &&
        party.role === 'DECD'
      );
    })
  );
}
export const isHiddenAccount = (account) => {
  return account.hiddenAccount ? account.hiddenAccount : false;
};

export function extractAndFormatOwners(accounts) {
  const firstAccount = isArray(accounts) ? accounts[0] : accounts;
  const [primary, non] = partition(firstAccount.owners, 'primaryOwner');
  const owners = {
    primaryOwners: sortBy(primary.map((o) => o.ownerName)).join(', '),
    secondaryOwners: sortBy(non.map((o) => o.ownerName)).join(', ')
  };
  return Object.values(owners)
    .filter((v) => v)
    .join(', ');
}

export const sortAccountsByRetirement = (a, b) => {
  if (a.retirementAccount !== b.retirementAccount) {
    return a.retirementAccount ? -1 : 1;
  }
  return 0;
};

export const sortAccountsByName = (a, b) => {
  // +(bool) converts the boolean to a 1 = true, 0 = false.
  // With those converted numbers we can sort an invalid input after a valid input
  const aIsEmpty = +(isEmpty(a) || isEmpty(a.accountName));
  const bIsEmpty = +(isEmpty(b) || isEmpty(b.accountName));

  if (aIsEmpty || bIsEmpty) {
    return aIsEmpty - bIsEmpty;
  }

  return a.accountName.localeCompare(b.accountName);
};

export const isPurgedAccount = (a) => a.purgedAccount;

export const isClosedAccount = (a) => a.closedAccount;

export const isUGMAUTMA = (a) => a.accountTypeId === UGMA_UTMA;

export const isRelated = (a) => {
  if (!a) {
    return;
  }
  return a.isRelated;
};

export const isZeroBalanceRASAccount = (account, historicalBalances) => {
  const balance =
    historicalBalances.selectedDate && historicalBalances.balances
      ? historicalBalances.balances.find(
          (currBalance) => currBalance.accountId === account.accountId
        ).endingMarketValue
      : account.balanceValue;
  return balance === 0;
};

export const isRasNonHouseholdAccount = (account, customerPlanningGroupId) =>
  isActiveAdvisory(account.productType) &&
  account.financialPlanningGroupId !== customerPlanningGroupId;
// AKA primaryParty
export const getAgreementParties = (a) => {
  if (!a || !a.agreementParties) {
    return;
  }
  return a.agreementParties.filter(
    (party) => party && toUpper(party.role) === 'OWNR'
  );
};

export const isPrimaryParty = (a) => {
  const parties = getAgreementParties(a);
  const primary = parties && parties.find((p) => p.primaryParty);
  return primary && primary.primaryParty;
};

export const generateRegistrationMap = (accounts) => {
  const map = [];
  accounts.forEach((account) => {
    const { registrationKey } = account;
    if (registrationKey) {
      const mappedKey = map[registrationKey] || [];
      mappedKey.push(account);
      map[registrationKey] = mappedKey;
    }
  });
  return map;
};

export const hasAsOfDate = (account) => account && account.balanceAsOfDate;

export const getAsOfDateFromList = (accounts, index) => {
  return accounts.length && accounts.length - 1 >= index
    ? accounts[index].balanceAsOfDate
    : null;
};

export const getIntraDayAsOfDate = (intradayAsOfDateMap, accountId) => {
  if (isEmpty(intradayAsOfDateMap)) {
    return null;
  }
  // if searching for any intraday as of date, return first one found
  if (!accountId) {
    return Object.values(intradayAsOfDateMap).find((d) => d != null);
  }
  return intradayAsOfDateMap[accountId] ? intradayAsOfDateMap[accountId] : null;
};

export const groupAccountsByAccountType = (productAccounts) =>
  groupBy(productAccounts, 'accountType');

export const determineAvailableAccountTypes = (productAccounts) =>
  Object.keys(groupAccountsByAccountType(productAccounts));

export const isOwnerOnAgreementByBPID = (account, bpid) => {
  const { agreementParties } = account;
  return (
    agreementParties &&
    agreementParties.find((agreementParty) => {
      return (
        agreementParty.customer &&
        agreementParty.customer.businessPartyId === bpid &&
        agreementParty.role === 'OWNR'
      );
    })
  );
};

export const isCustodian = (account, bpid) => {
  const { agreementParties } = account;
  return (
    agreementParties &&
    agreementParties.some((agreementParty) => {
      return (
        agreementParty.customer &&
        agreementParty.customer.businessPartyId === bpid &&
        agreementParty.role === 'CUST'
      );
    })
  );
};

export const isAddressOfRecordDeliveryType = (type) =>
  type === ADDRESS_OF_RECORD_DELIVERY_TYPE;
export const isBaseTransactionType = (type) => type === BASE_TRANSACTION_TYPE;
export const isElectronicDeliveryType = (type) =>
  type === ELECTRONIC_DELIVERY_TYPE;
export const isExchangeTransactionType = (type) =>
  type === EXCHANGE_TRANSACTION_TYPE;
export const isPurchaseTransactionType = (type) =>
  type === PURCHASE_TRANSACTION_TYPE;
export const isRedeemTransactionType = (type) =>
  type === REDEEM_TRANSACTION_TYPE;
export const isWireDeliveryType = (type) => type === WIRE_DELIVERY_TYPE;
export const isDistributionToSchoolTransactionType = (type) =>
  type === DISTRIBUTION_TO_SCHOOL_TYPE;
export const isDTSMailCheckToSchool = (type) =>
  type === ADDRESS_OF_RECORD_DELIVERY_TYPE;

export const hasServiceFeature = (account, featureID) => {
  if (!account || !featureID) {
    return false;
  }
  return (
    account.serviceIds &&
    account.serviceIds.length > 0 &&
    account.serviceIds.includes(featureID)
  );
};

export const hasBrokerageAdvantage = (services) =>
  services && services.length > 0 && services.some((serviceId) => serviceId === BROKERAGE_ADVANTAGE_SERVICE_ID);

export const hasStopTransfer = (account) =>
  hasServiceFeature(account, STOPPED_TRANSFER);

export const hasStopPurchase = (account) =>
  hasServiceFeature(account, STOP_PURCHASE);

export const hasOptionsTradingEnabled = (account) =>
  hasServiceFeature(account, OPTIONS_ENABLED);

export const hasBlockViewAccess = (account) =>
  hasServiceFeature(account, BLOCK_VIEW_ACCESS);

export const hasStopAll = (account) => hasServiceFeature(account, VRU_STOP_ALL);

export const intradayEligibleFilter = (accountDetails) =>
  isBrokerage(accountDetails.productType) && accountDetails.balance !== 0;

export const hasExternalRPSAccount = (account) => {
  const { productType, rpsPlanNumber } = account;
  const hasTracPlan =
    productType != null &&
    typeof productType != 'undefined' &&
    isTracPlan(productType);
  return (
    rpsPlanNumber &&
    rpsPlanNumber.length === 6 &&
    rpsPlanNumber.startsWith(6) &&
    hasTracPlan
  );
};

export const hasAccountLabel = ({
  shareClassConversion,
  isViewOnly,
  sweepAccount
}) => {
  const activeShareClassConverted =
    shareClassConversion && shareClassConversion.activeShareClassConverted;
  return activeShareClassConverted || isViewOnly || sweepAccount;
};

export const formatProductsForSharedLib = (simpleView) => {
  if (!simpleView) {
    return {
      MutualFund: simpleView.hasMutualFund
    };
  }
  return {
    MutualFund: simpleView.hasMutualFund,
    Brokerage: simpleView.hasBrokerage,
    hasMD529: simpleView.hasMaryland529,
    hasAK529: simpleView.hasAlaska529,
    hasNAT529: simpleView.hasNational529,
    APP: simpleView.hasActivePlus,
    RAS: simpleView.hasRAS
  };
};
